import React from 'react';

/**
 * Route/itinerary detail — the route header plus its Start / Pickup / Dropoff /
 * Break / End steps with times, locations and passengers.
 *
 * Extracted verbatim from Dashboard/Future.js's "ITINERARY VIEW" so the Bookings
 * screen and My Schedule show the same route details from one implementation
 * instead of two that can drift.
 */

// Itinerary step styling.
export const stepStyles = {
  start: { label: 'Start', color: '#16a34a' },
  end: { label: 'End', color: '#dc2626' },
  break: { label: 'Break', color: '#ca8a04' },
  hub: { label: 'Hub', color: '#9333ea' },
  pickup: { label: 'Pickup', color: '#2563eb' },
  delivery: { label: 'Dropoff', color: '#ea580c' },
};

export function getStyle(type) {
  if (!type) return stepStyles.pickup;
  const key = type.toLowerCase();
  if (key.includes('break')) return stepStyles.break;
  return stepStyles[key] || stepStyles.pickup;
}

function formatTime(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

/**
 * @param {object} schedule - { routeName, startDateTime, endDateTime, itinerary: [] }
 * @param {boolean} showHeader - hide when the surrounding screen already titles the route
 */
function RouteItineraryDetail({ schedule, showHeader = true }) {
  const steps = Array.isArray(schedule?.itinerary) ? schedule.itinerary : [];

  return (
    <div>
      {showHeader && (
        <div className="mb-3 flex items-center justify-between rounded-lg bg-white px-3 py-2 shadow">
          <span className="font-semibold text-gray-900">{schedule?.routeName}</span>
          <span className="text-sm text-gray-600">
            {formatTime(schedule?.startDateTime)} - {formatTime(schedule?.endDateTime)}
          </span>
        </div>
      )}

      {steps.length === 0 ? (
        <p className="rounded-lg bg-white p-4 text-center text-sm text-gray-500 shadow">
          This route has no stops.
        </p>
      ) : (
        <table className="min-w-full overflow-hidden rounded-lg bg-white text-sm shadow">
          <thead>
            <tr className="bg-gray-100 text-left text-xs font-medium uppercase text-gray-500">
              <th className="w-20 px-2 py-1.5">Time</th>
              <th className="w-16 px-2 py-1.5">Type</th>
              <th className="px-2 py-1.5">Location</th>
              <th className="px-2 py-1.5">Passenger</th>
            </tr>
          </thead>
          <tbody>
            {steps.map((step, index) => {
              const s = getStyle(step.type);
              const isTrip = step.type === 'pickup' || step.type === 'delivery';
              return (
                <tr key={step.stepId || index} className="border-t border-gray-100">
                  <td className="whitespace-nowrap px-2 py-1.5 font-medium text-gray-800">
                    {formatTime(step.time)}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1.5">
                    <span
                      className="rounded px-1.5 py-0.5 text-xs font-semibold"
                      style={{ color: s.color, backgroundColor: s.color + '18' }}
                    >
                      {s.label}
                    </span>
                  </td>
                  <td className="max-w-0 truncate px-2 py-1.5 text-gray-700">{step.locationName}</td>
                  <td className="max-w-0 truncate px-2 py-1.5 text-gray-500">
                    {isTrip ? step.passengerName || '' : ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default RouteItineraryDetail;
